import { type OidcMetadata, type User, UserManager, WebStorageStateStore } from 'oidc-client-ts';

let _um: UserManager | null = null;
// Synchronous mirror of the current user so `getOidcAccount()` (consumed
// synchronously by chrome components) and `AuthedLayout`'s boot gate don't
// need to await `getUser()`. Updated by the UserManager events wired below.
let _currentUser: User | null = null;
// Module-level idempotency guard replacing MSAL's `inProgress` machine.
// `signinRedirect` navigates the page away, so the only realistic double-fire
// is a React StrictMode re-invoke before navigation tears the page down.
let _redirecting = false;

export interface OidcAccountProjection {
  username: string;
  name: string | undefined;
  subject: string | undefined;
}

/**
 * Dev-only static OIDC metadata. Ping (auth.corelogic.com) serves no CORS
 * headers, so the two browser XHRs oidc-client-ts makes — the discovery
 * fetch and the token exchange — are blocked cross-origin from the Vite dev
 * origin. Supplying static metadata skips discovery entirely, and the token
 * exchange routes through the dev server's `/as` proxy entry
 * (vite.config.ts) so it arrives same-origin. The redirect endpoints stay on
 * the real host — full-page navigations are CORS-exempt. The endpoint paths
 * are PingFederate's stable defaults (mirrored from the live discovery
 * document). Production is discovery-driven and untouched: the `DEV` gate
 * compiles this branch out of prod builds.
 */
function devMetadata(authority: string): Partial<OidcMetadata> {
  return {
    issuer: authority,
    authorization_endpoint: `${authority}/as/authorization.oauth2`,
    token_endpoint: `${window.location.origin}/as/token.oauth2`,
    end_session_endpoint: `${authority}/idp/init_logout.openid`,
  };
}

export function buildUserManager(): UserManager {
  const authority = import.meta.env.VITE_OIDC_AUTHORITY;
  const clientId = import.meta.env.VITE_OIDC_CLIENT_ID;
  const scope = import.meta.env.VITE_API_SCOPE;
  if (!authority || !clientId || !scope) {
    throw new Error(
      'OIDC bootstrap aborted: missing VITE_OIDC_AUTHORITY, VITE_OIDC_CLIENT_ID, or VITE_API_SCOPE. ' +
        'Populate marketplace-ui/.env (see .env.example) and reload.',
    );
  }
  return new UserManager({
    authority, // oidc-client-ts auto-fetches ${authority}/.well-known/openid-configuration
    client_id: clientId,
    redirect_uri: `${window.location.origin}/auth/callback`,
    post_logout_redirect_uri: `${window.location.origin}/auth/logout`,
    response_type: 'code',
    scope, // 'openid email name'
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    automaticSilentRenew: true,
    ...(import.meta.env.DEV ? { metadata: devMetadata(authority) } : {}),
  });
}

export function setUserManager(um: UserManager): void {
  _um = um;
  um.events.addUserLoaded((u) => {
    _currentUser = u;
  });
  um.events.addUserUnloaded(() => {
    _currentUser = null;
  });
}

export function _resetForTests(): void {
  _um = null;
  _currentUser = null;
  _redirecting = false;
}

export function getUserManager(): UserManager {
  if (!_um) throw new Error('UserManager not set — initOidc must run before any API call');
  return _um;
}

export function getUserManagerForActions(): UserManager {
  return getUserManager();
}

/**
 * Boot-time OIDC setup. Constructs + wires the singleton, completes the
 * `/auth/callback` code exchange when present, and seeds the sync user mirror.
 */
export async function initOidc(): Promise<void> {
  const um = buildUserManager();
  setUserManager(um);
  if (window.location.pathname === '/auth/callback') {
    _currentUser = await um.signinRedirectCallback();
  } else {
    _currentUser = await um.getUser();
  }
}

export function getOidcAccount(): OidcAccountProjection | null {
  if (!_currentUser) return null;
  const p = _currentUser.profile;
  return {
    username: typeof p.email === 'string' ? p.email : (p.sub ?? ''),
    name: typeof p.name === 'string' ? p.name : undefined,
    subject: p.sub,
  };
}

/** Idempotent interactive redirect, replacing MSAL's inProgress gate. */
export async function redirectToSignIn(state?: string): Promise<void> {
  if (_redirecting) return;
  _redirecting = true;
  await getUserManager().signinRedirect(state !== undefined ? { state } : undefined);
}

export async function redirectToSignOut(): Promise<void> {
  await getUserManager().signoutRedirect();
}

// Exposed for the sync mirror used by tests to simulate a signed-in user.
export function _setCurrentUserForTests(user: User | null): void {
  _currentUser = user;
}
